import { Button, Chip } from "@heroui/react";
import { Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";

import { AddEditSeoSlugModal } from "../modal/add-edit";
import SeoSlugDeleteModal from "../modal/delete";

import type { DataGridColumn } from "@/components/kit";

import { DataTable, RowMenu, SearchBox, notify } from "@/components/kit";
import { Toolbar, formatDay } from "@/components/sales/shared";
import { SeoSlugRepository } from "@/repositories/seo-slug";
import { SeoSlug } from "@/types/seo-slug";

interface SeoSlugListTableProps {
  items: SeoSlug[];
  page: number;
  totalItems: number;
  changePage: (page: number) => void;
  isLoading: boolean;
  limit: number;
  onLimitChange: (limit: number) => void;
  searchTerm: string;
  onSearchChange: (searchTerm: string) => void;
}

export const SeoSlugListTable = ({
  items,
  page,
  totalItems,
  changePage,
  isLoading,
  limit,
  onLimitChange,
  searchTerm,
  onSearchChange,
}: SeoSlugListTableProps) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editing, setEditing] = useState<SeoSlug | null>(null);
  const [deleting, setDeleting] = useState<SeoSlug | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const seoSlugRepository = useRef(new SeoSlugRepository());

  const handleRegenerateSitemap = useCallback(async () => {
    setIsRegenerating(true);
    try {
      const result = await seoSlugRepository.current.regenerateSitemap();

      notify.success(
        "Sitemap regenerated",
        `Generated ${result.generated.length} sitemaps with ${result.productCount} products`,
      );
    } catch (error: any) {
      notify.error(
        "Sitemap regeneration failed",
        error.response?.data?.error || error.message,
      );
    } finally {
      setIsRegenerating(false);
    }
  }, []);

  const columns = useMemo<DataGridColumn<SeoSlug>[]>(
    () => [
      {
        id: "slug",
        header: "Slug",
        isRowHeader: true,
        minWidth: 220,
        cell: (item) => (
          <div className="flex flex-col items-start leading-snug">
            <span className="font-mono text-sm font-medium">{item.slug}</span>
            <span className="text-muted text-xs" dir="rtl" lang="ar">
              {item.slugAr}
            </span>
          </div>
        ),
      },
      {
        id: "metaTitle",
        header: "Meta title",
        minWidth: 280,
        cell: (item) => <span>{item.metaTitle}</span>,
      },
      {
        id: "isActive",
        header: "Status",
        width: 120,
        cell: (item) => (
          <Chip
            color={item.isActive ? "success" : "default"}
            variant="soft"
          >
            {item.isActive ? "Active" : "Inactive"}
          </Chip>
        ),
      },
      {
        id: "updatedAt",
        header: "Updated",
        width: 140,
        cell: (item) => (
          <span className="text-muted tabular-nums">
            {formatDay(item.updatedAt)}
          </span>
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        align: "end",
        width: 64,
        cell: (item) => (
          <RowMenu
            items={[
              {
                id: "edit",
                label: "Edit",
                icon: <Pencil className="size-4" />,
                onAction: () => setEditing(item),
              },
              {
                id: "delete",
                label: "Delete",
                icon: <Trash2 className="size-4" />,
                danger: true,
                onAction: () => setDeleting(item),
              },
            ]}
          />
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Toolbar
        end={
          <>
            <Button
              isPending={isRegenerating}
              variant="outline"
              onPress={handleRegenerateSitemap}
            >
              <RefreshCw className="size-4" />
              Regenerate Sitemap
            </Button>
            <Button onPress={() => setIsAdding(true)}>
              <Plus className="size-4" />
              Add SEO Slug
            </Button>
          </>
        }
      >
        <SearchBox
          className="w-80"
          placeholder="Search SEO slugs"
          value={searchTerm}
          onChange={onSearchChange}
        />
      </Toolbar>

      <DataTable
        columns={columns}
        compact
        empty={{
          title: "No SEO slugs found",
          description: "Try a different search, or add a slug.",
        }}
        getRowId={(item) => item.id}
        isLoading={isLoading}
        label="SEO slugs"
        pagination={{
          page,
          pageSize: limit,
          total: totalItems,
          onPageChange: changePage,
          onPageSizeChange: onLimitChange,
          pageSizes: [10, 20, 50, 100],
        }}
        rows={items}
      />

      <AddEditSeoSlugModal
        isEdit={false}
        isOpen={isAdding}
        onClose={() => setIsAdding(false)}
      />
      {editing ? (
        <AddEditSeoSlugModal
          isEdit
          isOpen
          seoSlug={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {deleting ? (
        <SeoSlugDeleteModal
          isOpen
          seoSlug={deleting}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
};
